import React, { useEffect, useMemo, useState } from 'react';
import { FiEdit2, FiTrash2, FiSun, FiMoon, FiDownload, FiGrid, FiImage, FiFileText } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import {
  getUserTransactions,
  addTransaction,
  getAllTransactions,
  deleteTransaction,
} from '../services/dataService';
import * as customerService from '../services/customerService';
import * as walletService from '../services/walletService';
import { exportCustomerTransactionsPDF, exportAllClientsSaldosPDF } from '../services/exportService';
import { canAccessModule } from '../services/authService';
// Using public logo asset

function formatNumber(n) {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);
}

function formatDate(ts) {
  try {
    if (ts === null || ts === undefined || ts === '') return '';
    let d;
    // Detect numeric (string or number) unix timestamps
    if (typeof ts === 'number' || /^\d+$/.test(String(ts))) {
      const num = Number(ts);
      // < 10^12 → seconds, otherwise milliseconds
      d = new Date(num < 1e12 ? num * 1000 : num);
    } else {
      d = new Date(ts);
    }
    if (isNaN(d.getTime())) return '' + ts;
    return d.toLocaleString();
  } catch {
    return '' + ts;
  }
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, tPt, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { goToPicker, setActiveModule } = useModule();
  const [query, setQuery] = useState('');
  const [txSearchQuery, setTxSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [txType, setTxType] = useState('deposit'); // deposit | withdraw
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [txCurrency, setTxCurrency] = useState('USD');
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingSaldos, setExportingSaldos] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientModalMode, setClientModalMode] = useState('create'); // 'create' | 'edit'
  const [editingUserId, setEditingUserId] = useState(null);
  const [newName, setNewName] = useState('');
  const [newInfo, setNewInfo] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [showTotals, setShowTotals] = useState(false);
  // Transaction History module
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyResults, setHistoryResults] = useState([]);
  const [historyTotals, setHistoryTotals] = useState({});
  const [historyError, setHistoryError] = useState('');
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo, setHistDateTo] = useState('');
  const [histCustomerId, setHistCustomerId] = useState('');
  const [histCurrency, setHistCurrency] = useState('');
  const [histType, setHistType] = useState('');
  const [txDate, setTxDate] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`; // datetime-local format
  });

  // Load initial users from API
  useEffect(() => {
    const loadCustomers = async () => {
      const result = await customerService.listCustomers();
      if (result.success && result.data) {
        setUsers(result.data);
        if (result.data.length && !selectedId) setSelectedId(result.data[0].id);
      }
    };
    loadCustomers();
  }, []);

  // Handle search
  useEffect(() => {
    const handle = setTimeout(async () => {
      if (query.trim()) {
        const result = await customerService.searchCustomers(query);
        if (result.success && result.data) {
          setUsers(result.data);
          if (result.data.length && (!selectedId || !result.data.find((r) => r.id === selectedId))) {
            setSelectedId(result.data[0].id);
          }
        }
      } else {
        const result = await customerService.listCustomers();
        if (result.success && result.data) {
          setUsers(result.data);
        }
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const selectedUser = useMemo(() => (selectedId ? users.find(u => u.id == selectedId) : null), [selectedId, users]);
  const [tx, setTx] = useState([]);
  const [balances, setBalances] = useState({ USD: 0, EUR: 0, SRD: 0 });

  // Fetch wallet balances and transactions from API when selected user changes
  useEffect(() => {
    if (selectedId) {
      const fetchWalletData = async () => {
        // Fetch balances
        const walletBalances = await walletService.getWalletBalances(selectedId);
        setBalances(walletBalances);
        
        // Fetch transactions
        const txResult = await walletService.getCustomerTransactions(selectedId);
        if (txResult.success && txResult.data) {
          // Convert unix timestamps to milliseconds for Date objects
          const transactions = txResult.data.map(t => ({
            ...t,
            date: parseInt(t.created_at) * 1000, // Convert to milliseconds
            amount: parseFloat(t.amount)
          }));
          setTx(transactions);
        } else {
          setTx([]);
        }
      };
      fetchWalletData();
    } else {
      setBalances({ USD: 0, EUR: 0, SRD: 0 });
      setTx([]);
    }
  }, [selectedId]);

  // Handle transaction search
  useEffect(() => {
    if (!selectedId) return;
    
    const handle = setTimeout(async () => {
      if (txSearchQuery.trim()) {
        const result = await walletService.searchTransactions(selectedId, txSearchQuery);
        if (result.success && result.data) {
          const transactions = result.data.map(t => ({
            ...t,
            date: parseInt(t.created_at) * 1000,
            amount: parseFloat(t.amount)
          }));
          setTx(transactions);
        }
      } else {
        // If search is empty, fetch all transactions
        const txResult = await walletService.getCustomerTransactions(selectedId);
        if (txResult.success && txResult.data) {
          const transactions = txResult.data.map(t => ({
            ...t,
            date: parseInt(t.created_at) * 1000,
            amount: parseFloat(t.amount)
          }));
          setTx(transactions);
        }
      }
    }, 300);
    
    return () => clearTimeout(handle);
  }, [txSearchQuery, selectedId]);

  // Global totals across all clients (fetched from API)
  const [globalTotals, setGlobalTotals] = useState({ USD: 0, EUR: 0, SRD: 0 });

  // Edit transaction modal state
  const [showEditTx, setShowEditTx] = useState(false);
  const [editingTx, setEditingTx] = useState(null); // original tx object
  const [editTxAmount, setEditTxAmount] = useState('');
  const [editTxType, setEditTxType] = useState('deposit');
  const [editTxDescription, setEditTxDescription] = useState('');
  const [editTxSubmitting, setEditTxSubmitting] = useState(false);

  const refreshTxAndBalances = async () => {
    if (!selectedId) return;
    const walletBalances = await walletService.getWalletBalances(selectedId);
    setBalances(walletBalances);
    const txResult = await walletService.getCustomerTransactions(selectedId);
    if (txResult.success && txResult.data) {
      setTx(txResult.data.map(tr => ({
        ...tr,
        date: parseInt(tr.created_at) * 1000,
        amount: parseFloat(tr.amount)
      })));
    }
  };

  const openEditTransaction = (txr) => {
    setEditingTx(txr);
    setEditTxAmount(String(Math.abs(parseFloat(txr.amount) || 0).toFixed(2)));
    setEditTxType(txr.type || 'deposit');
    setEditTxDescription(txr.description || '');
    setShowEditTx(true);
  };

  const closeEditTransaction = () => {
    setShowEditTx(false);
    setEditingTx(null);
  };

  const handleSaveEditTransaction = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!editingTx) return;
    const amt = parseFloat(editTxAmount);
    if (!Number.isFinite(amt)) {
      alert(t('invalid_amount') || 'Invalid amount');
      return;
    }
    setEditTxSubmitting(true);
    try {
      const result = await walletService.editTransaction(editingTx.id, {
        amount: amt,
        type: editTxType,
        description: editTxDescription,
      });
      if (result.success) {
        await refreshTxAndBalances();
        closeEditTransaction();
      } else {
        alert(result.message || 'Failed to edit transaction');
      }
    } finally {
      setEditTxSubmitting(false);
    }
  };

  const adjustAmount = (delta) => {
    setAmount((prev) => {
      const v = parseFloat(prev || '0');
      const base = Number.isFinite(v) ? v : 0;
      const next = Math.max(0, base + delta);
      return next.toFixed(2);
    });
  };

  const handleDeleteTransaction = async (id) => {
    if (!id) return;
    if (!window.confirm(t('delete_tx_confirm'))) return;
    
    const result = await walletService.deleteTransaction(id);
    
    if (result.success) {
      // Refresh wallet balances
      const walletBalances = await walletService.getWalletBalances(selectedId);
      setBalances(walletBalances);
      
      // Refresh transactions
      const txResult = await walletService.getCustomerTransactions(selectedId);
      if (txResult.success && txResult.data) {
        const transactions = txResult.data.map(t => ({
          ...t,
          date: parseInt(t.created_at) * 1000,
          amount: parseFloat(t.amount)
        }));
        setTx(transactions);
      }
    } else {
      alert(result.message || 'Failed to delete transaction');
    }
  };

  const openTotals = async () => {
    setShowTotals(true);
    // Fetch all clients balance from API
    const result = await walletService.getAllClientsBalance();
    if (result.success && result.data) {
      setGlobalTotals(result.data);
    }
  };
  const closeTotals = () => setShowTotals(false);

  // Convert a date input value (YYYY-MM-DD) to a unix-seconds boundary.
  // edge: 'start' -> 00:00:00, 'end' -> 23:59:59 of that day in local time.
  const dateInputToUnix = (value, edge) => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    const dt = edge === 'end'
      ? new Date(y, m - 1, d, 23, 59, 59)
      : new Date(y, m - 1, d, 0, 0, 0);
    return Math.floor(dt.getTime() / 1000);
  };

  const runHistorySearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const filters = {};
      const from = dateInputToUnix(histDateFrom, 'start');
      const to = dateInputToUnix(histDateTo, 'end');
      if (from !== null) filters.date_from = from;
      if (to !== null) filters.date_to = to;
      if (histCustomerId) filters.customer_id = histCustomerId;
      if (histCurrency) filters.currency = histCurrency;
      if (histType) filters.type = histType;

      const result = await walletService.getTransactionsHistory(filters);
      if (result.success && result.data) {
        setHistoryResults(result.data.transactions || []);
        setHistoryTotals(result.data.totals || {});
      } else {
        setHistoryError(result.message || 'Failed to load history');
        setHistoryResults([]);
        setHistoryTotals({});
      }
    } catch (err) {
      console.error('History search error:', err);
      setHistoryError('Network error. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    // Default to today
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    setHistDateFrom(todayStr);
    setHistDateTo(todayStr);
    setHistCustomerId('');
    setHistCurrency('');
    setHistType('');
    setHistoryResults([]);
    setHistoryTotals({});
    setHistoryError('');
    // Auto-run for today
    setHistoryLoading(true);
    try {
      const result = await walletService.getTransactionsHistory({
        date_from: dateInputToUnix(todayStr, 'start'),
        date_to: dateInputToUnix(todayStr, 'end'),
      });
      if (result.success && result.data) {
        setHistoryResults(result.data.transactions || []);
        setHistoryTotals(result.data.totals || {});
      }
    } finally {
      setHistoryLoading(false);
    }
  };
  const closeHistory = () => setShowHistory(false);

  const setHistoryQuickRange = (preset) => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    let from = today;
    const to = today;
    if (preset === 'today') {
      from = today;
    } else if (preset === '7d') {
      from = new Date(today);
      from.setDate(from.getDate() - 6);
    } else if (preset === '30d') {
      from = new Date(today);
      from.setDate(from.getDate() - 29);
    } else if (preset === 'month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (preset === 'all') {
      setHistDateFrom('');
      setHistDateTo('');
      return;
    }
    setHistDateFrom(fmt(from));
    setHistDateTo(fmt(to));
  };

  const openAddClient = () => {
    setClientModalMode('create');
    setEditingUserId(null);
    setNewName('');
    setNewInfo('');
    setNewPhone('');
    setShowClientModal(true);
  };

  const closeClientModal = () => setShowClientModal(false);

  const openEditClientFor = (user) => {
    if (!user) return;
    setClientModalMode('edit');
    setEditingUserId(user.id);
    setNewName(user.name || '');
    setNewInfo(user.info || user.email || '');
    setNewPhone(user.phone || '');
    setShowClientModal(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    
    if (clientModalMode === 'edit' && editingUserId) {
      const result = await customerService.updateCustomer(editingUserId, { name, info: newInfo, phone: newPhone });
      if (result.success) {
        const refreshResult = await customerService.listCustomers();
        if (refreshResult.success) setUsers(refreshResult.data);
      }
    } else {
      const result = await customerService.addCustomer({ name, info: newInfo, phone: newPhone });
      if (result.success && result.data) {
        setSelectedId(result.data.id);
        const refreshResult = await customerService.listCustomers();
        if (refreshResult.success) setUsers(refreshResult.data);
      }
    }
    setShowClientModal(false);
  };

  // Note: Edit/Delete actions are only available in the customers list (left panel)

  const handleDeleteClientById = async (id) => {
    if (!id) return;
    if (!window.confirm(t('delete_confirm'))) return;
    
    const result = await customerService.deleteCustomer(id);
    if (result.success) {
      const refreshResult = await customerService.listCustomers();
      if (refreshResult.success && refreshResult.data) {
        setUsers(refreshResult.data);
        if (selectedId === id) setSelectedId(refreshResult.data[0]?.id || null);
      }
    }
  };

  // Shortcut: open the Receipts module with the Custody type preselected and
  // prefilled — client name, the 3 saldos, and the last withdrawal + deposit.
  const handleMakeReceipt = () => {
    if (!selectedUser) return;
    const sorted = [...tx].sort((a, b) => (b.date || 0) - (a.date || 0));
    const last = sorted[0]; // the single most recent transaction
    const num = (n) => String(Number(n) || 0);

    const values = {
      cliente: selectedUser.name || '',
      saldo_usd: num(balances.USD),
      saldo_srd: num(balances.SRD),
      saldo_eur: num(balances.EUR),
    };
    if (last) {
      const amt = parseFloat(last.amount) || 0;
      const isWithdraw = (last.type || '').toLowerCase().includes('withdraw') || amt < 0;
      const cur = (last.currency || 'USD').toUpperCase();
      if (isWithdraw) {
        values.ultima_retirada = num(Math.abs(amt));
        values.ultima_retirada_cur = cur;
      } else {
        values.ultimo_deposito = num(Math.abs(amt));
        values.ultimo_deposito_cur = cur;
      }
    }

    setActiveModule('receipts', { receipt: { type: 'custodia', values } });
  };

  // Generate a phone-sized PNG report: client info + saldos + last 5 transactions.
  // Drawn on a canvas (no extra deps) and downloaded.
  const handleGenerateReport = () => {
    if (!selectedUser) return;

    // Fit as many recent transactions as keep the image phone-portrait sized.
    const MAX_ROWS = 15;
    const recent = [...tx]
      .sort((a, b) => (b.date || 0) - (a.date || 0))
      .slice(0, MAX_ROWS);

    // Layout (CSS px); rendered at 2x for a crisp image.
    const W = 720;
    const pad = 44;
    const scale = 2;
    const rowH = 46;
    const headH = 150;
    const clientH = 92;
    const saldoH = 232;
    const txHeadH = 46;
    const footH = 70;
    const H = pad + headH + clientH + saldoH + txHeadH + Math.max(1, recent.length) * rowH + footH + pad;

    const COL = {
      green: '#16a34a', greenDk: '#0f6e32', accent: '#f5d06c',
      text: '#1e293b', muted: '#64748b', pos: '#16a34a', neg: '#dc2626',
      bg: '#ffffff', card: '#f0f8f2', line: '#e2e8f0',
    };

    const canvas = document.createElement('canvas');
    canvas.width = W * scale;
    canvas.height = H * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);

    const money = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

    const draw = (logo) => {
      // Background
      ctx.fillStyle = COL.bg;
      ctx.fillRect(0, 0, W, H);
      // Top accent band
      ctx.fillStyle = COL.green;
      ctx.fillRect(0, 0, W, 8);

      let y = pad;

      // Header: logo + store
      if (logo) {
        try { ctx.drawImage(logo, pad, y, 84, 84); } catch { /* ignore */ }
      }
      const tx0 = pad + (logo ? 100 : 0);
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = COL.green;
      ctx.font = 'bold 30px Arial, sans-serif';
      ctx.fillText('GL Compra de Ouro', tx0, y + 34);
      ctx.fillStyle = COL.muted;
      ctx.font = '16px Arial, sans-serif';
      ctx.fillText('ANAMOESTRAAT #15', tx0, y + 60);
      y += headH - 40;

      // Accent divider
      ctx.strokeStyle = COL.accent;
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(pad, y); ctx.lineTo(W - pad, y); ctx.stroke();
      y += 34;

      // Client
      ctx.fillStyle = COL.text;
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillText(selectedUser.name || '', pad, y);
      y += 26;
      ctx.fillStyle = COL.muted;
      ctx.font = '16px Arial, sans-serif';
      const sub = [selectedUser.info || selectedUser.email, selectedUser.phone].filter(Boolean).join('  •  ');
      if (sub) { ctx.fillText(sub, pad, y); }
      y += 40;

      // Saldos card
      ctx.fillStyle = COL.card;
      ctx.fillRect(pad, y, W - pad * 2, saldoH - 20);
      ctx.fillStyle = COL.muted;
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.fillText(t('current_balance').toUpperCase(), pad + 20, y + 30);
      let sy = y + 66;
      [['USD', balances.USD], ['SRD', balances.SRD], ['EUR', balances.EUR]].forEach(([code, val]) => {
        ctx.fillStyle = COL.text;
        ctx.font = 'bold 20px Arial, sans-serif';
        ctx.fillText(code, pad + 20, sy);
        ctx.fillStyle = (Number(val) || 0) >= 0 ? COL.pos : COL.neg;
        ctx.font = 'bold 24px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(money(val), W - pad - 20, sy);
        ctx.textAlign = 'left';
        sy += 46;
      });
      y += saldoH;

      // Transactions
      ctx.fillStyle = COL.green;
      ctx.font = 'bold 18px Arial, sans-serif';
      ctx.fillText(t('report_recent'), pad, y);
      y += 20;
      ctx.strokeStyle = COL.line;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(pad, y); ctx.lineTo(W - pad, y); ctx.stroke();
      y += 26;

      if (recent.length === 0) {
        ctx.fillStyle = COL.muted;
        ctx.font = 'italic 15px Arial, sans-serif';
        ctx.fillText(t('no_transactions'), pad, y);
        y += rowH;
      } else {
        recent.forEach((r) => {
          const amt = parseFloat(r.amount) || 0;
          const cur = r.currency || 'USD';
          const sign = amt >= 0 ? '+' : '-';
          // left: date + description
          ctx.fillStyle = COL.text;
          ctx.font = '15px Arial, sans-serif';
          const d = new Date(r.date);
          const dstr = isNaN(d.getTime()) ? '' : d.toLocaleDateString();
          const desc = (r.description || '').slice(0, 40);
          ctx.fillText(dstr + (desc ? '  ' + desc : ''), pad, y);
          // right: amount
          ctx.fillStyle = amt >= 0 ? COL.pos : COL.neg;
          ctx.font = 'bold 16px Arial, sans-serif';
          ctx.textAlign = 'right';
          ctx.fillText(`${sign} ${money(Math.abs(amt))} ${cur}`, W - pad, y);
          ctx.textAlign = 'left';
          // row divider
          ctx.strokeStyle = COL.line;
          ctx.beginPath(); ctx.moveTo(pad, y + 14); ctx.lineTo(W - pad, y + 14); ctx.stroke();
          y += rowH;
        });
      }

      // Footer
      ctx.fillStyle = COL.muted;
      ctx.font = '13px Arial, sans-serif';
      ctx.fillText(`${t('generated')}: ${new Date().toLocaleString()}`, pad, H - pad + 6);

      // Download
      const safe = (selectedUser.name || 'client').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      const stamp = new Date().toISOString().slice(0, 10);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${safe}_${stamp}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, 'image/png');
    };

    // Load the logo, then draw (draw without it on failure).
    const img = new Image();
    img.onload = () => draw(img);
    img.onerror = () => draw(null);
    img.src = process.env.PUBLIC_URL + '/assets/logo.png';
  };

  const handleExportPDF = async () => {
    if (!selectedId) return;
    setExporting(true);
    try {
      const result = await exportCustomerTransactionsPDF(selectedId, tPt);
      if (!result.success) {
        alert(result.message || 'Failed to export PDF');
      }
    } catch (error) {
      console.error('Export PDF error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportSaldos = async () => {
    setExportingSaldos(true);
    try {
      const result = await exportAllClientsSaldosPDF(tPt);
      if (!result.success) {
        alert(result.message || 'Failed to export saldos');
      }
    } catch (error) {
      console.error('Export saldos error:', error);
      alert('Failed to export saldos. Please try again.');
    } finally {
      setExportingSaldos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    const amt = Number(amount);
    if (!amt || amt <= 0) return;

    setSubmitting(true);
    try {
      let result;
      if (txType === 'withdraw') {
        result = await walletService.withdrawal(selectedId, txCurrency, amt, description || 'Withdrawal');
      } else {
        result = await walletService.deposit(selectedId, txCurrency, amt, description || 'Deposit');
      }

      if (result.success) {
        // Clear form
        setAmount('');
        setDescription('');
        setTxCurrency('USD');
        
        // Refresh wallet data
        const walletBalances = await walletService.getWalletBalances(selectedId);
        setBalances(walletBalances);
        
        // Refresh transactions
        const txResult = await walletService.getCustomerTransactions(selectedId);
        if (txResult.success && txResult.data) {
          const transactions = txResult.data.map(t => ({
            ...t,
            date: parseInt(t.created_at) * 1000,
            amount: parseFloat(t.amount)
          }));
          setTx(transactions);
        }
      } else {
        alert(result.message || 'Transaction failed');
      }
    } catch (error) {
      console.error('Transaction error:', error);
      alert('Transaction failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="layout">
      <header className="app-header">
        <div className="brand">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="Logo" />
          <h1 className="app-title">{t('brand_title')}</h1>
        </div>
        <div className="app-actions">
          <button className="btn btn-ghost btn-icon" onClick={goToPicker} title={t('back_to_modules')} aria-label={t('back_to_modules')}>
            <FiGrid />
          </button>
          <button className="btn btn-ghost" onClick={openTotals} title={t('all_clients_total')}>
            {t('all_clients_total')}
          </button>
          <button
            className="btn btn-ghost"
            onClick={handleExportSaldos}
            disabled={exportingSaldos}
            title={t('export_saldos')}
          >
            <FiDownload style={{ marginRight: '6px' }} />
            {exportingSaldos ? t('exporting') : t('export_saldos')}
          </button>
          <button className="btn btn-ghost" onClick={openHistory} title={t('transaction_history')}>
            {t('transaction_history')}
          </button>
          <button
            className="btn btn-ghost btn-icon"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <select
            className="lang-select"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            aria-label="Language"
            title="Language"
          >
            <option value="en">English</option>
            <option value="pt-BR">Português (BR)</option>
          </select>
          <span className="muted">{t('signed_in_as')} {user?.username}</span>
          <button className="btn btn-ghost" onClick={logout}>{t('logout')}</button>
        </div>
      </header>

      <main className="content">
        <section className="panel users">
          <div className="panel-header">
            <div className="panel-title">
              <h2>{t('customers')}</h2>
              <button
                type="button"
                className="btn btn-primary btn-icon"
                onClick={openAddClient}
                title={t('add_client')}
                aria-label={t('add_client')}
              >
                +
              </button>
            </div>
            <input
              className="search-input"
              type="text"
              placeholder={t('search_placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="list">
            {users.map((u) => (
              <div
                key={u.id}
                className={"user-item" + (selectedId === u.id ? ' active' : '')}
                onClick={() => setSelectedId(u.id)}
                role="button"
                tabIndex={0}
                title={`${u.name} — ${u.info || ''}`}
              >
                <div className="user-initials">{(u.name || '?')
                  .split(' ')
                  .map((s) => s[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}</div>
                <div className="user-meta">
                  <div className="user-name">{u.name}</div>
                  <div className="user-sub muted">{u.info || u.email}</div>
                </div>
                <div className="user-actions-inline" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => openEditClientFor(u)} title={t('edit')} aria-label={t('edit')}>
                    <FiEdit2 />
                  </button>
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => handleDeleteClientById(u.id)} title={t('delete')} aria-label={t('delete')}>
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
            {users.length === 0 && (
              <div className="empty muted">{t('no_customers')}</div>
            )}
          </div>
        </section>

        <section className="panel details">
          {selectedUser ? (
            <>
              <div className="details-header">
                <div>
                  <h2>{selectedUser.name}</h2>
                  <div className="muted small">{selectedUser.info || selectedUser.email} • {selectedUser.phone}</div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={handleExportPDF}
                      disabled={exporting}
                      title={t('export_pdf')}
                    >
                      <FiDownload style={{ marginRight: '6px' }} />
                      {exporting ? t('exporting') : t('export_pdf')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={handleGenerateReport}
                      title={t('report_image')}
                    >
                      <FiImage style={{ marginRight: '6px' }} />
                      {t('report_image')}
                    </button>
                    {canAccessModule('receipts', user) && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={handleMakeReceipt}
                        title={t('make_receipt')}
                      >
                        <FiFileText style={{ marginRight: '6px' }} />
                        {t('make_receipt')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="balance-card">
                  <div className="label muted">{t('current_balance')}</div>
                  <div className="balance-rows">
                    <div className={"row " + (balances.USD >= 0 ? 'positive' : 'negative')}>
                      <span className="code">USD</span>
                      <span className="val">{formatNumber(balances.USD)}</span>
                    </div>
                    <div className={"row " + (balances.EUR >= 0 ? 'positive' : 'negative')}>
                      <span className="code">EUR</span>
                      <span className="val">{formatNumber(balances.EUR)}</span>
                    </div>
                    <div className={"row " + (balances.SRD >= 0 ? 'positive' : 'negative')}>
                      <span className="code">SRD</span>
                      <span className="val">{formatNumber(balances.SRD)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="two-col">
                <div className="card">
                  <h3>{t('make_transaction')}</h3>
                  <form onSubmit={handleSubmit} className="form-grid">
                    <label>
                      <span>{t('type')}</span>
                      <div className="segmented" role="tablist" aria-label="Transaction type">
                        <button
                          type="button"
                          className={"segmented-item" + (txType === 'deposit' ? ' active' : '')}
                          onClick={() => setTxType('deposit')}
                          role="tab"
                          aria-selected={txType === 'deposit'}
                        >
                          {t('deposit')}
                        </button>
                        <button
                          type="button"
                          className={"segmented-item" + (txType === 'withdraw' ? ' active' : '')}
                          onClick={() => setTxType('withdraw')}
                          role="tab"
                          aria-selected={txType === 'withdraw'}
                        >
                          {t('withdraw')}
                        </button>
                      </div>
                    </label>
                    <label>
                      <span>{t('amount')}</span>
                      <div className="input-group amount">
                        <select className="currency-select" value={txCurrency} onChange={(e) => setTxCurrency(e.target.value)}>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="SRD">SRD</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                        />
                        <div className="steppers">
                          <button type="button" className="step-btn" onClick={() => adjustAmount(-10)} title="-10">
                            –
                          </button>
                          <button type="button" className="step-btn" onClick={() => adjustAmount(10)} title="+10">
                            +
                          </button>
                        </div>
                      </div>
                    </label>
                    <label className="wide">
                      <span>{t('description')}</span>
                      <div className="input-group">
                        <span className="prefix" aria-hidden>📝</span>
                        <input
                          type="text"
                          placeholder={t('description_placeholder')}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>
                    </label>
                    <label>
                      <span>{t('date')}</span>
                      <div className="input-group">
                        <span className="prefix" aria-hidden>📅</span>
                        <input
                          type="datetime-local"
                          value={txDate}
                          onChange={(e) => setTxDate(e.target.value)}
                          required
                        />
                      </div>
                    </label>
                    <div className="actions">
                      <button className="btn btn-primary" type="submit" disabled={submitting}>
                        {submitting ? 'Saving…' : t('add_transaction')}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="card card-tight">
                  <h3>{t('transaction_history')}</h3>
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Search transactions..."
                    value={txSearchQuery}
                    onChange={(e) => setTxSearchQuery(e.target.value)}
                    style={{ marginBottom: '12px' }}
                  />
                  <div className="transactions">
                    {tx.map((txr) => {
                      const signClass = (txr.amount >= 0 ? 'positive' : 'negative');
                      const cur = txr.currency || 'USD';
                      return (
                        <div className="tx-item" key={txr.id}>
                          <div className="tx-main">
                            <div className="currency-group">
                              <span className={"currency-chip " + signClass}><span className="code">{cur}</span> <span className="val">{formatNumber(Math.abs(txr.amount))}</span></span>
                            </div>
                            <div className="user-actions-inline" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="btn btn-ghost btn-icon" title={t('edit')} aria-label={t('edit')} onClick={() => openEditTransaction(txr)}>
                                <FiEdit2 />
                              </button>
                              <button type="button" className="btn btn-ghost btn-icon" title={t('delete')} aria-label={t('delete')} onClick={() => handleDeleteTransaction(txr.id)}>
                                <FiTrash2 />
                              </button>
                            </div>
                            <div className="tx-desc">{txr.description || '—'}</div>
                          </div>
                          <div className="tx-sub muted small">{formatDate(txr.date)} • <span className="muted">{cur}</span></div>
                        </div>
                      );
                    })}
                    {tx.length === 0 && <div className="empty muted">{t('no_transactions')}</div>}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="empty muted">{t('select_customer')}</div>
          )}
        </section>
      </main>
      {showEditTx && editingTx && (
        <div className="modal-backdrop" onClick={closeEditTransaction}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="editTxTitle">
            <div className="modal-header">
              <h3 id="editTxTitle">{t('edit_transaction')}</h3>
            </div>
            <div className="modal-body">
              <form
                onSubmit={handleSaveEditTransaction}
                style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
              >
                <div className="muted small">
                  {t('date')}: {formatDate(editingTx.date || (parseInt(editingTx.created_at) * 1000))}
                  {' • '}
                  <span className="muted">{editingTx.currency}</span>
                </div>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span>{t('type')}</span>
                  <select
                    value={editTxType}
                    onChange={(e) => setEditTxType(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="deposit">{t('deposit')}</option>
                    <option value="withdrawal">{t('withdraw')}</option>
                  </select>
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span>{t('amount')}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={editTxAmount}
                    onChange={(e) => setEditTxAmount(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span>{t('description')}</span>
                  <input
                    type="text"
                    value={editTxDescription}
                    onChange={(e) => setEditTxDescription(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </label>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginTop: '4px',
                  }}
                >
                  <button type="button" className="btn btn-ghost" onClick={closeEditTransaction} disabled={editTxSubmitting}>{t('cancel')}</button>
                  <button type="submit" className="btn btn-primary" disabled={editTxSubmitting}>
                    {editTxSubmitting ? (t('saving') || 'Saving…') : t('save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showTotals && (
        <div className="modal-backdrop" onClick={closeTotals}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="totalsTitle">
            <div className="modal-header">
              <h3 id="totalsTitle">{t('all_clients_total')}</h3>
            </div>
            <div className="modal-body">
              <div className="currency-group">
                <span className={"currency-chip " + (globalTotals.USD >= 0 ? 'positive' : 'negative')}>
                  <span className="code">USD</span>
                  <span className="val">{globalTotals.USD < 0 ? '−' : ''}{formatNumber(Math.abs(globalTotals.USD))}</span>
                </span>
                <span className={"currency-chip " + (globalTotals.EUR >= 0 ? 'positive' : 'negative')}>
                  <span className="code">EUR</span>
                  <span className="val">{globalTotals.EUR < 0 ? '−' : ''}{formatNumber(Math.abs(globalTotals.EUR))}</span>
                </span>
                <span className={"currency-chip " + (globalTotals.SRD >= 0 ? 'positive' : 'negative')}>
                  <span className="code">SRD</span>
                  <span className="val">{globalTotals.SRD < 0 ? '−' : ''}{formatNumber(Math.abs(globalTotals.SRD))}</span>
                </span>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={closeTotals}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
      {showHistory && (
        <div className="modal-backdrop" onClick={closeHistory}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="historyTitle"
            style={{ maxWidth: '1100px', width: '95%' }}
          >
            <div className="modal-header">
              <h3 id="historyTitle">{t('transaction_history')}</h3>
            </div>
            <div className="modal-body">
              <form
                onSubmit={runHistorySearch}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span className="muted small">{t('date_from')}</span>
                  <input type="date" value={histDateFrom} onChange={(e) => setHistDateFrom(e.target.value)} />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span className="muted small">{t('date_to')}</span>
                  <input type="date" value={histDateTo} onChange={(e) => setHistDateTo(e.target.value)} />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span className="muted small">{t('client')}</span>
                  <select value={histCustomerId} onChange={(e) => setHistCustomerId(e.target.value)}>
                    <option value="">{t('all')}</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span className="muted small">{t('currency')}</span>
                  <select value={histCurrency} onChange={(e) => setHistCurrency(e.target.value)}>
                    <option value="">{t('all')}</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="SRD">SRD</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span className="muted small">{t('type')}</span>
                  <select value={histType} onChange={(e) => setHistType(e.target.value)}>
                    <option value="">{t('all')}</option>
                    <option value="deposit">{t('deposit')}</option>
                    <option value="withdrawal">{t('withdraw')}</option>
                  </select>
                </label>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={historyLoading}
                  style={{ height: '44px', alignSelf: 'end' }}
                >
                  {historyLoading ? t('loading') : t('search')}
                </button>
              </form>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHistoryQuickRange('today')}>{t('today')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHistoryQuickRange('7d')}>{t('last_7_days')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHistoryQuickRange('30d')}>{t('last_30_days')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHistoryQuickRange('month')}>{t('this_month')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHistoryQuickRange('all')}>{t('all_time')}</button>
              </div>

              {historyError && <div className="error" style={{ marginBottom: '8px' }}>{historyError}</div>}

              <div className="muted small" style={{ marginBottom: '8px' }}>
                {historyResults.length} {t('results')}
                {Object.keys(historyTotals).length > 0 && (
                  <span style={{ marginLeft: '12px' }}>
                    {Object.entries(historyTotals).map(([cur, val]) => (
                      <span key={cur} style={{ marginRight: '10px' }}>
                        <strong>{cur}</strong> net: {val >= 0 ? '+' : ''}{formatNumber(val)}
                      </span>
                    ))}
                  </span>
                )}
              </div>

              <div style={{ maxHeight: '50vh', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--card-bg)' }}>
                <table className="tx-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--text)' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--panel-2)', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px' }}>{t('date')}</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>{t('client')}</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>{t('type')}</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>{t('description')}</th>
                      <th style={{ textAlign: 'center', padding: '8px' }}>{t('currency')}</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>{t('amount')}</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>{t('balance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyResults.length === 0 && !historyLoading && (
                      <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: '16px' }}>{t('no_results')}</td></tr>
                    )}
                    {historyResults.map((tx) => {
                      const amt = parseFloat(tx.amount);
                      return (
                        <tr key={tx.id} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{formatDate(tx.created_at)}</td>
                          <td style={{ padding: '8px' }}>{tx.customer_name || `#${tx.customer_id}`}</td>
                          <td style={{ padding: '8px', textTransform: 'capitalize' }}>{tx.type}</td>
                          <td style={{ padding: '8px' }}>{tx.description || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{tx.currency}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: amt >= 0 ? 'var(--positive)' : 'var(--negative)', fontWeight: 600 }}>
                            {amt >= 0 ? '+' : '-'}{formatNumber(Math.abs(amt))}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatNumber(tx.balance_after)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={closeHistory}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}
      {showClientModal && (
        <div className="modal-backdrop" onClick={closeClientModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="clientModalTitle">
            <div className="modal-header">
              <h3 id="clientModalTitle">{clientModalMode === 'edit' ? t('edit_client') : t('new_client')}</h3>
            </div>
            <form className="modal-body" onSubmit={handleSaveClient}>
              <label>
                <span>{t('name')}</span>
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('name')} required />
              </label>
              <label>
                <span>{t('additional_info')}</span>
                <input type="text" value={newInfo} onChange={(e) => setNewInfo(e.target.value)} placeholder={t('additional_info')} />
              </label>
              <label>
                <span>{t('phone')}</span>
                <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder={t('phone')} />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeClientModal}>{t('cancel')}</button>
                <button type="submit" className="btn btn-primary">{clientModalMode === 'edit' ? t('save') : t('create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
